import axios from 'axios';
import { message } from 'antd'; // 示例：Element Plus 提示（可替换为其他UI库）
import { history } from '@umijs/max';

// 1. 根据环境配置不同的baseURL
const getBaseURL = () => {
    const env = process.env.REACT_APP_ENV || 'dev';
    // 移除环境变量中前后的双引号
    const envKey = env.replace(/^"|"$/g, '');
    // console.log('envKey', envKey);
    // 配置不同环境的API地址
    const baseURLMap = {
        dev: '/system-api', // 开发环境
        test: 'http://8.136.56.97:8082/system-api', // 测试环境
        pre: 'https://api.keepushealthy.cn/system', // 预发布环境
        prod: 'https://api.example.com/system', // 生产环境
    };
    return baseURLMap[envKey] || '';
};

// 2. 创建axios实例，配置基础参数
const service = axios.create({
    baseURL: getBaseURL(), // 根据环境动态设置baseURL
    timeout: 60000, // 超时时间：60秒
    headers: {
        'Content-Type': 'application/json;charset=utf-8' // 默认请求头
    }
});

// 2. 取消请求控制器（用于重复请求取消）
const pendingMap = new Map(); // 存储待取消的请求
/**
 * 生成请求唯一标识
 * @param {Object} config - axios配置
 * @returns {String} 标识字符串
 */
const getPendingKey = (config) => {
    const { url, method, params, data } = config;
    return [url, method, JSON.stringify(params), JSON.stringify(data)].join('&');
};

/**
 * 添加待取消的请求
 * @param {Object} config - axios配置
 */
const addPending = (config) => {
    const key = getPendingKey(config);
    config.cancelToken = config.cancelToken || new axios.CancelToken((cancel) => {
        if (!pendingMap.has(key)) {
            pendingMap.set(key, cancel);
        }
    });
};

/**
 * 移除并取消待执行的请求
 * @param {Object} config - axios配置
 */
const removePending = (config) => {
    const key = getPendingKey(config);
    if (pendingMap.has(key)) {
        const cancel = pendingMap.get(key);
        cancel(`重复请求被取消：${config.url}`); // 取消请求并提示
        pendingMap.delete(key);
    }
};

// 3. 请求拦截器：请求发送前处理
service.interceptors.request.use(
    (config) => {
        // 1. 取消重复请求
        removePending(config);
        addPending(config);

        // 2. 添加token（示例：从localStorage获取）
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`; // JWT token格式
            config.headers.token = token; // JWT token格式
        }

        // 3. GET请求参数处理（可选：防止缓存）
        if (config.method?.toUpperCase() === 'GET' && config.params) {
            config.params._t = Date.now(); // 添加时间戳
        }

        // 4. 自定义请求头（如接口版本号）
        config.headers['X-API-Version'] = 'v1';
        // console.log('请求拦截器配置：', config);
        return config;
    },
    (error) => {
        // 请求配置错误处理
        console.error('请求拦截器错误：', error);
        return Promise.reject(error);
    }
);

// 4. 响应拦截器：响应返回后处理
service.interceptors.response.use(
    (response) => {
        // 1. 移除已完成请求的取消标识
        removePending(response.config);

        // 2. 特殊处理：文件流响应直接返回完整 response（需要从 headers 获取文件名）
        if (response.config.responseType === 'blob') {
            return response; // 返回完整的 response，便于处理文件名
        }

        const { data } = response; // 解构响应体
        // 3. 业务码校验（根据后端约定调整）
        const code = data.code || 200;
        const msg = data.message || '操作成功';

        // 成功码（示例：后端约定200为成功）
        if (code === 200) {
            return data; // 直接返回业务数据，简化上层调用
        }
        // 4. 业务错误处理（非200的情况）
        switch (code) {
            case 401: // 未登录/Token过期
                message.error('登录状态已过期，请重新登录')
                localStorage.clear(); // 清除失效token
                history.replace({ pathname: '/user/login' });
                break;
            case 403: // 权限不足
                message.error('暂无权限访问，请联系管理员');
                break;
            case 404: // 接口不存在
                message.error('请求资源不存在');
                break;
            case 500: // 服务器错误
                message.error('服务器内部错误，请稍后重试');
                break;
            default: // 其他业务错误
                message.error(msg || '请求失败');
        }

        // 业务错误返回reject，上层可catch处理
        return Promise.reject(new Error(msg || '请求失败'));
    },
    (error) => {
        // 1. 移除失败请求的取消标识
        if (error.config) {
            removePending(error.config);
        }

        // 2. 错误分类处理
        let errorMsg = '请求失败';
        if (axios.isCancel(error)) {
            // 取消请求的错误（不提示，仅打印）
            console.warn('请求已取消：', error.message);
            return Promise.reject(error);
        } else if (error.message.includes('timeout')) {
            // 超时错误
            errorMsg = '请求超时，请检查网络';
            message.error(errorMsg);
        } else if (error.message.includes('Network Error')) {
            // 网络错误
            errorMsg = '网络异常，请检查网络连接';
            message.error(errorMsg);
        } else {
            // HTTP状态码错误（如401/403/500）
            const status = error.response?.status;
            errorMsg = `请求错误 [${status}]：${error.response?.data?.msg || '未知错误'}`;
            message.error(errorMsg);
        }

        // 统一返回错误对象
        return Promise.reject({
            status: error.response?.status || 0,
            message: errorMsg,
            original: error // 保留原始错误对象，方便上层排查
        });
    }
);
// 5. 导出封装后的axios实例
export default service